import { useEffect, useMemo, useRef } from "react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";

import { useTranslations } from "@/i18n";
import { useLeaderboard, useMatchCount, useStatusHistory } from "@/store/selectors";
import { theme } from "@/ui/theme";

// ── Styled ───────────────────────────────────────────────────

const Shell = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  background: ${theme.gradients.floodlight};
  padding: 3vh 4vw;
  overflow: hidden;
  user-select: none;
  cursor: none;
`;

const Title = styled.h1`
  text-align: center;
  font-family: ${theme.fonts.display};
  font-size: 4vh;
  font-weight: 800;
  letter-spacing: -0.03em;
  margin-bottom: 2.5vh;
  flex-shrink: 0;
`;

const Board = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.2vh;
  overflow: hidden;
`;

const Row = styled(motion.div)<{ isFirst?: boolean }>`
  display: flex;
  align-items: center;
  gap: 2vw;
  padding: 1.5vh 2vw;
  border-radius: ${theme.radii["2xl"]};
  border: 1px solid
    ${(p) =>
      p.isFirst
        ? `color-mix(in oklab, ${theme.colors.gold} 50%, transparent)`
        : theme.colors.border};
  background: ${(p) =>
    p.isFirst
      ? `color-mix(in oklab, ${theme.colors.gold} 8%, transparent)`
      : `color-mix(in oklab, ${theme.colors.bgCard} 70%, transparent)`};
  backdrop-filter: blur(8px);
  box-shadow: ${theme.shadow.card};
  min-height: 7vh;
`;

const RankCircle = styled.div<{ color?: string; dim?: boolean }>`
  width: 5vh;
  height: 5vh;
  border-radius: ${theme.radii.full};
  display: grid;
  place-items: center;
  font-family: ${theme.fonts.display};
  font-weight: 800;
  font-size: 2.5vh;
  flex-shrink: 0;
  background: ${(p) => p.color ?? "transparent"};
  color: ${(p) => (p.dim ? theme.colors.textDim : p.color ? "#000" : theme.colors.text)};
`;

const PlayerName = styled.div<{ isFirst?: boolean }>`
  flex: 1;
  font-family: ${theme.fonts.display};
  font-size: 3.5vh;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${(p) => (p.isFirst ? theme.colors.gold : theme.colors.text)};
`;

const Points = styled.div`
  font-family: ${theme.fonts.display};
  font-size: 3.5vh;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  min-width: 12vw;
  text-align: right;
`;

const Streak = styled.div`
  font-size: 3vh;
  min-width: 8vw;
  text-align: center;
`;

const TickerWrap = styled.div`
  flex-shrink: 0;
  margin-top: auto;
  padding-top: 1.5vh;
  border-top: 1px solid ${theme.colors.border};
  overflow: hidden;
  height: 5vh;
  display: flex;
  align-items: center;
  position: relative;
`;

const TickerTrack = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
  will-change: transform;
`;

const TickerItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 1vw;
  padding: 0 3vw;
  font-size: 2.2vh;
  color: ${theme.colors.textMuted};
  white-space: nowrap;
  flex-shrink: 0;
`;

const TickerTime = styled.span`
  font-variant-numeric: tabular-nums;
  color: ${theme.colors.primary};
  font-weight: 600;
  font-size: 1.8vh;
`;

const TickerDot = styled.span`
  color: ${theme.colors.textDim};
  font-size: 1.5vh;
`;

const EmptyShell = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3vh;
  color: ${theme.colors.textDim};
`;

// ── Helpers ──────────────────────────────────────────────────

const RANK_COLORS: Record<number, string> = {
  1: theme.colors.gold,
  2: theme.colors.silver,
  3: theme.colors.bronze,
};

const getStreakDisplay = (winStreak: number): string => {
  if (winStreak >= 2) return "🔥".repeat(Math.min(winStreak, 5));
  return "";
};

const formatTime = (ts: number): string => {
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

// ── News Ticker ──────────────────────────────────────────────

const TICKER_SPEED = 60; // px per second

const NewsTicker = ({
  items,
  fallback,
}: {
  items: { text: string; timestamp: number }[];
  fallback: string;
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);
  const offsetRef = useRef(0);
  const contentWidthRef = useRef(0);

  // Measure content width whenever items change
  useEffect(() => {
    if (!trackRef.current) return;
    contentWidthRef.current = trackRef.current.scrollWidth / 2;
  }, [items, fallback]);

  useEffect(() => {
    lastTimeRef.current = performance.now();

    const animate = (now: number) => {
      const delta = (now - lastTimeRef.current) / 1000;
      lastTimeRef.current = now;

      let next = offsetRef.current + TICKER_SPEED * delta;
      const w = contentWidthRef.current;
      if (w > 0 && next >= w) next -= w;
      offsetRef.current = next;

      if (trackRef.current) {
        trackRef.current.style.transform = `translateX(-${next}px)`;
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const renderItems = () => {
    if (items.length === 0) {
      return (
        <>
          <TickerItem>{fallback}</TickerItem>
          <TickerDot>◆</TickerDot>
        </>
      );
    }
    return items.map((item, i) => (
      <span key={`${item.timestamp}-${i}`} style={{ display: "contents" }}>
        <TickerItem>
          <TickerTime>{formatTime(item.timestamp)}</TickerTime>
          {item.text}
        </TickerItem>
        <TickerDot>◆</TickerDot>
      </span>
    ));
  };

  return (
    <TickerWrap>
      <TickerTrack ref={trackRef}>
        {renderItems()}
        {renderItems()}
      </TickerTrack>
    </TickerWrap>
  );
};

// ── Component ────────────────────────────────────────────────

export default function TVLeaderboard() {
  const sortedPlayers = useLeaderboard();
  const matchCount = useMatchCount();
  const statusHistory = useStatusHistory();
  const { t } = useTranslations();

  // Filter to today's events
  const todayItems = useMemo(() => {
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    return statusHistory.filter((e) => e.timestamp >= startOfDay);
  }, [statusHistory]);

  const fallbackText = matchCount === 0 ? t("tv.noMatchesYet") : t("tv.matchesPlayed", matchCount);

  if (sortedPlayers.length === 0) {
    return (
      <Shell>
        <Title>{t("tv.title")}</Title>
        <EmptyShell>{t("tv.waitingForPlayers")}</EmptyShell>
      </Shell>
    );
  }

  const showRanks = matchCount > 0;

  return (
    <Shell>
      <Board>
        {sortedPlayers.map((player, i) => {
          const rank = i + 1;
          const isFirst = rank === 1 && showRanks;

          return (
            <Row
              key={player.id}
              isFirst={isFirst}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.06 }}
            >
              {showRanks && (
                <RankCircle color={RANK_COLORS[rank]} dim={rank > 3}>
                  {rank}
                </RankCircle>
              )}
              <PlayerName isFirst={isFirst}>
                {player.name}
                {isFirst && " 👑"}
              </PlayerName>
              <Points>{Math.round(player.totalPoints)} pts</Points>
              <Streak>{getStreakDisplay(player.stats.winStreak) || "-"}</Streak>
            </Row>
          );
        })}
      </Board>

      <NewsTicker items={todayItems} fallback={fallbackText} />
    </Shell>
  );
}
